<template>
  <v-container fluid class="request-access pa-0">

    <!-- HERO -->
    <section class="hero d-flex align-center justify-center">
      <v-container>
        <v-row justify="center">
          <v-col cols="12" md="8" lg="6">

            <!-- ANIMACIÓN -->
            <v-slide-y-transition appear>
              <v-card class="form-card pa-8">


                <div class="text-center mb-6">
                  <v-btn text color="primary" class="mb-2 top-home-link" to="/">
                    <v-icon left>mdi-home-outline</v-icon>
                    {{ $t('common.backHome') }}
                  </v-btn>

                  <v-img :src="require('@/assets/logo.png')" contain height="90" class="mx-auto mb-4" />




                  <h1 class="font-weight-bold mb-1">
                    {{ $t('requestAccess.title') }}
                  </h1>

                  <p class="grey--text">
                    {{ $t('requestAccess.subtitle') }}
                  </p>
                </div>

                <!-- INFO REVISION -->
                <v-alert type="info" outlined dense color="primary" class="mb-6">
                  {{ $t('requestAccess.reviewBefore') }}
                  <strong>{{ $t('requestAccess.reviewStrong') }}</strong>.
                  {{ $t('requestAccess.reviewAfter') }}
                </v-alert>



                <!-- FORM -->
                <v-form ref="form" v-model="valid">

                  <v-text-field v-model="form.name" :label="$t('requestAccess.name')" outlined prepend-inner-icon="mdi-account"
                    :rules="[rules.required]" required />

                  <v-text-field v-model="form.email" :label="$t('requestAccess.email')" outlined prepend-inner-icon="mdi-email" type="email"
                    :rules="[rules.required, rules.email]" required />

                  <v-text-field v-model="form.company" :label="$t('requestAccess.company')" outlined prepend-inner-icon="mdi-domain"
                    :rules="[rules.required]" required />

                  <v-text-field v-model="form.address" :label="$t('requestAccess.address')" outlined
                    prepend-inner-icon="mdi-map-marker" />

                  <v-text-field v-model="form.phone" :label="$t('requestAccess.phone')" outlined prepend-inner-icon="mdi-phone" />

                  <v-radio-group
                    v-model="form.accessMethod"
                    :label="$t('requestAccess.accessMethod')"
                    :rules="[rules.required]"
                    row
                    required
                    class="access-method"
                  >
                    <v-radio :label="$t('requestAccess.accessMethodGoogle')" value="google" />
                    <v-radio :label="$t('requestAccess.accessMethodPassword')" value="password" />
                  </v-radio-group>

                  <v-textarea v-model="form.notes" :label="$t('requestAccess.notes')" outlined rows="3"
                    prepend-inner-icon="mdi-text" />

                  <!-- LEGAL -->
                  <v-checkbox
                    v-model="form.accepted"
                    :rules="[rules.accepted]"
                    required
                    class="legal-acceptance"
                  >
                    <template #label>
                      <span>
                        {{ $t('requestAccess.legalAcceptPrefix') }}
                        <router-link to="/legal-advice" target="_blank" rel="noopener" @click.native.stop>
                          {{ $t('requestAccess.legalNoticeLink') }}
                        </router-link>,
                        <router-link to="/privacy-policy" target="_blank" rel="noopener" @click.native.stop>
                          {{ $t('requestAccess.privacyLink') }}
                        </router-link>
                        {{ $t('requestAccess.legalAcceptAnd') }}
                        <router-link to="/cookies" target="_blank" rel="noopener" @click.native.stop>
                          {{ $t('requestAccess.cookiesLink') }}
                        </router-link>.
                        <span class="legal-version">v{{ legalTermsVersion }}</span>
                      </span>
                    </template>
                  </v-checkbox>


                  <!-- SUBMIT -->
                  <v-btn block large color="primary" class="mt-6" :loading="loading" @click="submit">
                    {{ $t('requestAccess.submit') }}
                  </v-btn>

                  <!-- SUCCESS -->
                  <v-alert v-if="sent" type="success" dense class="mt-6">
                    {{ $t('requestAccess.successBefore') }}
                    {{ $t('requestAccess.successAfter') }}
                  </v-alert>

                  <v-alert v-if="errorMessage" type="error" dense border="left" class="mt-6">
                    {{ errorMessage }}
                  </v-alert>

                  <!-- CONTACT -->
                  <p class="text-center grey--text text--darken-1 mt-6">
                    {{ $t('requestAccess.question') }}
                    {{ $t('requestAccess.writeUs') }}
                    <a href="mailto:valkiria.lab.apps@gmail.com" class="contact-link">
                      valkiria.lab.apps@gmail.com
                    </a>
                  </p>

                </v-form>

              </v-card>
            </v-slide-y-transition>

          </v-col>
        </v-row>
      </v-container>
    </section>

  </v-container>
</template>

<script>
import { db } from "@/firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { getUserErrorMessage } from "@/utils/errors";
import { LEGAL_TERMS_VERSION, LEGAL_TEXT_VERSIONS } from "@/config/legal";

const emptyForm = () => ({
  name: "",
  email: "",
  company: "",
  address: "",
  phone: "",
  accessMethod: "",
  notes: "",
  accepted: false
});

export default {
  name: "RequestAccess",
  data() {
    return {
      valid: false,
      loading: false,
      sent: false,
      errorMessage: "",
      form: emptyForm(),
      rules: {
        required: v => !!v || this.$t('requestAccess.required'),
        email: v => /.+@.+\..+/.test(v) || this.$t('requestAccess.invalidEmail'),
        accepted: v => v || this.$t('requestAccess.acceptedRequired')
      }
    }
  },
  computed: {
    legalTermsVersion() {
      return LEGAL_TERMS_VERSION;
    }
  },
methods: {
  async submit () {
    if (!this.$refs.form.validate()) return;

    this.loading = true;
    this.errorMessage = "";

    try {
      // 🧾 Guardar solicitud en Firestore
      await addDoc(collection(db, "requests"), {
        name: this.form.name,
        email: this.form.email,
        company: this.form.company,
        address: this.form.address,
        phone: this.form.phone,
        accessMethod: this.form.accessMethod,
        notes: this.form.notes,
        status: "pending",
        legalAcceptedAt: serverTimestamp(),
        legalAcceptedVersion: LEGAL_TERMS_VERSION,
        legalAcceptedTextVersions: LEGAL_TEXT_VERSIONS,
        createdAt: serverTimestamp()
      });

      // ✅ Feedback UI
      this.sent = true;
      this.form = emptyForm();
      this.$nextTick(() => {
        this.$refs.form.resetValidation();
      });

    } catch (error) {
      console.error("Error al enviar solicitud:", error);
      this.errorMessage = getUserErrorMessage(error, "requestAccess.submitError", this.$i18n);
    } finally {
      this.loading = false;
    }
  }
}

}
</script>

<style scoped>
/* Fondo */
.request-access {
  min-height: 100vh;
  background: linear-gradient(135deg, #7b2ff7, #ffffff);
}

/* Hero */
.hero {
  min-height: 100vh;
}

/* Card */
.form-card {
  border-radius: 22px;
  box-shadow: 0 30px 70px rgba(0, 0, 0, 0.28);
}

/* Contact link */
.contact-link {
  color: #4f46e5;
  font-weight: 600;
  text-decoration: none;
}

.contact-link:hover {
  text-decoration: underline;
}

.top-home-link {
  font-weight: 700;
  letter-spacing: 0;
}

.access-method {
  margin-top: 0;
}

.legal-acceptance {
  color: #5f5870;
}

.legal-acceptance a {
  color: #4f46e5;
  font-weight: 700;
  text-decoration: none;
}

.legal-acceptance a:hover {
  text-decoration: underline;
}

.legal-version {
  color: #7b728f;
  font-size: 12px;
  font-weight: 700;
  margin-left: 4px;
}

@media (max-width: 600px) {
  .hero {
    align-items: flex-start !important;
    padding: 12px 0;
  }

  .form-card {
    border-radius: 16px;
    padding: 22px 16px !important;
  }

  .form-card h1 {
    font-size: 1.45rem;
    line-height: 1.25;
  }

  .access-method ::v-deep .v-input--radio-group__input {
    align-items: flex-start;
    flex-direction: column;
  }

  .legal-acceptance ::v-deep .v-label {
    font-size: 0.85rem;
    line-height: 1.4;
  }

  .contact-link {
    overflow-wrap: anywhere;
  }
}
</style>
